import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { HeaderService } from "@app/components/header/header.service";
import {
    Homeowner,
    SsoHomeownerApiService,
} from "@app/services/sso/sso-homeowner-api.service";
import { AuthTokenService } from "@app/services/auth-token.service";
import { NavigationService } from "@app/services/navigation.service";
import { SpinnerService } from "@app/services/spinner.service";
import { OauthService } from "@app/services/oauth.service";

const FORMAT_ERROR = "Enter the 6-digit numeric passcode.";
const OTP_ERROR = "The code you entered is incorrect or expired. Please try again.";

const MAX_ATTEMPTS = 3;

@Component({
    selector: "app-existing-sso-profile-merge",
    template: require("./existing-sso-profile-merge.component.html"),
})
export class ExistingSsoProfileMergeComponent implements OnInit {
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private auth = inject(AuthTokenService);
    private headerService = inject(HeaderService);
    private api = inject(SsoHomeownerApiService);
    private oauth = inject(OauthService);
    private navigationService = inject(NavigationService);
    private spinnerService = inject(SpinnerService);

    // The arriving SSO homeowner performing the merge (path userId) + the selected
    // existing profile picked on the search screen: its id is the OTP-validate
    // target, and its email is where the passcode was sent / what we display.
    private userId = this.auth.getSsoIdentity().userId;
    private profileId = this.auth.getSsoSelectedProfileId();

    resent = false; // a new passcode was sent - shows the sent note
    codeError = false; // entered code was incorrect or expired
    lockedOut = false; // too many wrong tries — must request a new code
    private attempts = 0;

    form: FormGroup = this.fb.group({
        // numeric, exactly 6 digits.
        code: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    ngOnInit(): void {
        this.headerService.setDisplayRightContent(false);
    }

    // Enabled only for a valid 6-digit code; AC4: stays disabled after lock-out.
    get mergeDisabled(): boolean {
        return this.form.get("code")!.invalid || this.lockedOut;
    }

    // Whether to show the inline error (red border + message).
    get showError(): boolean {
        const control = this.form.get("code")!;
        return this.codeError || (control.invalid && control.touched);
    }

    // Message wins when the server rejected the code; otherwise format error.
    get errorMessage(): string {
        return this.codeError ? OTP_ERROR : FORMAT_ERROR;
    }

    // Numeric-only, capped at 6 digits; clears the server error as they retype.
    onCodeInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digits = input.value.replace(/\D/g, "").slice(0, 6);
        input.value = digits;
        this.form.get("code")!.setValue(digits);
        this.codeError = false;
    }

    // Validate the code; on success proceed to the merge flow.
    merge(): void {
        if (this.form.invalid || this.lockedOut) {
            this.form.markAllAsTouched();
            return;
        }
        const otp = String(this.form.get("code")!.value);
        this.spinnerService.showSpinner();
        this.api
            .validateChallenge(this.userId, this.profileId, otp)
            .subscribe(result => {
                if (result.authenticated) {
                    this.completeMerge();
                } else {
                    this.handleInvalidCode();
                }
            });
    }

    // Request a fresh code, resets the attempt counter + lock, updates the text.
    resendCode(event: Event): void {
        event.preventDefault();
        this.api.sendChallenge(this.userId, this.profileId).subscribe(() => {
            this.attempts = 0;
            this.lockedOut = false;
            this.codeError = false;
            this.form.get("code")!.reset("");
            this.resent = true;
        });
    }

    // Abandon the OTP session and return to the search results.
    returnToPrevious(): void {
        this.router.navigate(["/sso/search-profile"]);
    }

    // OTP authenticated → merge the SSO user into the existing profile, then
    // sign in. `merge` links the arriving homeowner (userId) to the selected
    // existing profile (profileId) on the backend.
    private completeMerge(): void {
        this.codeError = false;
        this.api
            .mergeSsoHomeowner(this.userId, this.profileId)
            .subscribe((merged) => {
                this.finishAfterMerge(merged);
            });
    }

    // Since merge reassigns the homeowner id, the merged id differs from the id
    // our current token carries (userId); the token is stale - re-authenticate, so a
    // a fresh token is minted before landing in the /properties.
    private finishAfterMerge(merged: Homeowner): void {
        const mergedId = merged?.id != null ? String(merged.id) : "";
        if (mergedId && mergedId != this.userId) {
            this.reauthenticate();
        } else {
            this.redirectToProperties();
        }
    }

    // There is no refresh_token in implicit oauth flow, so re-run authorize
    // which essentially redirects, and okta bounces back through the callback, which
    // mints a fresh token with new merged hoahomewowner id
    private reauthenticate(): void {
        this.oauth.authenticate().subscribe();
    }

    // Full-page redirect to the registered homepage.
    private redirectToProperties(): void {
        this.navigationService.navigateToProperties()
    }

    // Wrong/expired code -> error, clear the field, count the attempt, and
    // after MAX_ATTEMPTS lock the Merge button until a new code is requested.
    private handleInvalidCode(): void {
        this.spinnerService.closeSpinner();
        this.attempts += 1;
        this.codeError = true;
        this.form.get("code")!.reset("");
        if (this.attempts >= MAX_ATTEMPTS) {
            this.lockedOut = true;
        }
    }
}
